import API from "../Services/BaseUrl";
import {
  importRsaPublicKey,
  generateAesKey,
  encryptPayloadWithAes,
  encryptAesKeyWithRsa,
  decryptResponseWithAes,
  generateNonce,
} from "./cryptoUtils";

// RSA public key is rotated server-side every 30 min; cache for 25 to stay
// safely inside the window. We hold the Promise itself (not the resolved
// value) so concurrent callers on page load DEDUPLICATE to a single
// /users/key request — otherwise the rate limiter (10 req/60s per IP)
// trips when 5+ parallel encryptedPost calls fire on mount.
let keyPromise = null;
let keyFetchedAt = 0;
const CACHE_TTL = 25 * 60 * 1000;

/**
 * Seed the key cache from an already-fetched server key (e.g., the
 * `nextKey` field on the login response). Lets post-login encrypted
 * calls skip the extra `/users/key` round-trip.
 *
 * @param {{ id: string, key: string }} serverKey
 */
export const primeKeyCache = (serverKey) => {
  if (!serverKey || !serverKey.id || !serverKey.key) return;
  keyPromise = Promise.resolve(serverKey);
  keyFetchedAt = Date.now();
};

/**
 * Wipe the cached RSA public key. Called when the server returns "Encryption
 * key expired" — forces the next encryptedPost (or loginApi retry) to refetch
 * a fresh key instead of replaying the stale one.
 */
export const clearKeyCache = () => {
  keyPromise = null;
  keyFetchedAt = 0;
};

/**
 * Bypass the CACHE_TTL guard and refetch the RSA public key in the
 * background. Called by the token timer (tokenUtils.js) when the user is
 * active and the cached key is nearing rotation, so encryptedPost never
 * has to fall back to the retry path. Safe: in-flight awaits on the
 * previous keyPromise still resolve with the old (still server-valid)
 * key, since Promise identity is captured at await time.
 */
export const refreshKeyProactive = () => {
  keyFetchedAt = 0;
  return fetchKey();
};

const fetchKey = () => {
  const now = Date.now();
  if (keyPromise && now - keyFetchedAt < CACHE_TTL) {
    return keyPromise; // every concurrent caller awaits the same in-flight promise
  }
  keyFetchedAt = now;
  keyPromise = API.post("/users/key")
    .then((response) => response.data.data) // { id, key }
    .catch((err) => {
      // Failed — clear so the next call retries instead of returning a stale reject
      keyPromise = null;
      keyFetchedAt = 0;
      throw err;
    });
  return keyPromise;
};

// Server returns this when the page/tab idled past the server's 30-min
// key rotation window. Substring match covers all three variants the
// server currently emits: "Encryption key expired.", "Key expired.",
// "Key expired. Please refresh." Does NOT match the sibling -401 errors
// ("Request expired" for stale timestamp, "Invalid request" for nonce
// replay) — those are genuine client-side bugs, not retriable.
const isKeyExpired = (data) =>
  data?.errCode === -401
  && /key expired/i.test(data?.message || data?.errMsg || "");

/**
 * Encrypted POST request — reusable for any endpoint.
 * Encrypts the request body using AES-256-GCM, wraps the AES key with RSA-OAEP,
 * and decrypts the encrypted server response using the same AES key.
 *
 * @param {string} url  - API endpoint path (e.g. "/dashboard/data")
 * @param {object} body - Request payload to encrypt (default: empty object)
 * @returns {object} Decrypted ApiResponse object returned by the server
 */
export const encryptedPost = async (url, body = {}, _isRetry = false) => {
  // 1. Get RSA public key (cached for up to 25 minutes)
  const { id, key: serverKey } = await fetchKey();

  // 2. Import RSA public key and generate a fresh AES-256-GCM session key
  const rsaPublicKey = await importRsaPublicKey(serverKey);
  const aesKey = await generateAesKey();

  // 3. Inject anti-replay fields (nonce + timestamp) into the payload
  const securedBody = {
    ...body,
    _nonce: generateNonce(),
    _ts: Date.now(),
  };

  // 4. Encrypt the secured body with AES-GCM
  const payload = await encryptPayloadWithAes(securedBody, aesKey);

  // 5. Wrap the AES key with RSA-OAEP so the server can unwrap it
  const wrappedKey = await encryptAesKeyWithRsa(aesKey, rsaPublicKey);

  // 6. POST the encrypted envelope to the server
  const response = await API.post(url, { id, key: wrappedKey, payload });

  // 7a. The cached RSA public key was rotated server-side while this tab
  //     sat idle (server TTL is 30 min, client cache is 25 min — but a
  //     paused JS event loop can carry a stale key across the boundary).
  //     Wipe the cache and retry once transparently before the caller
  //     even sees the error.
  if (!_isRetry && isKeyExpired(response.data)) {
    clearKeyCache();
    return encryptedPost(url, body, true);
  }

  // 7b. Decrypt the response when the server returns an encrypted payload
  //     Server wraps encrypted data inside: { errCode: 0, data: { data: "<base64>" } }
  if (response.data?.errCode === 0 && response.data?.data?.data) {
    return await decryptResponseWithAes(response.data.data.data, aesKey);
  }

  // Plain (non-encrypted) response — error responses may not be encrypted
  return response.data;
};
